import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { queryStringForAlertResult } from "@app2/alert/alert-inbox/utils";
import { Domain } from "@app2/alert/constants";
import { AlertClientService } from "@app2/clients/alert-client.service";
import { SearchClientService } from "@app2/clients/search-client.service";
import {
    AnomalyAlertResult,
    InboxAlert,
    QueryTrigger,
    SequenceTriggerAlertResult,
    SequenceTriggerAlertResultWithId,
    TriggerType,
    UbaAlertResult,
} from "@app2/clients/types/alert-typedefs";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { SavedQueryService } from "@app2/search/saved-query.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { RouterService } from "@app2/shared/services/router.service";
import { TimeService } from "@app2/shared/services/time.service";
import { SavedQuery } from "@app2/type-defs/search/search-types";
import { getMessageForError } from "@app2/util/errors/handleable-errors";
import { saveAs } from "file-saver";
import * as moment from "moment";
import { Observable } from "rxjs";
import { shareReplay, take } from "rxjs/operators";
import { StateParams } from "../../fraud/type-defs";

@Component({
    selector: "single-alert-display",
    template: `
        <ng-container *dsLoading="loading || innerLoading">
            <div *ngIf="!alert" class="vh-center-content" test-class="no-alert-selected">
                {{ "SELECT_NEW_ALERT" | localize }}
            </div>
            <div *ngIf="alert" class="inbox-right-scrollable">
                <div class="flex-container space-between alert-display-header">
                    <trigger-header class="alert-display-trigger-header"
                                    *ngIf="trigger"
                                    [currentTrigger]="trigger"
                                    [showNameLink]="true"
                                    test-id="selected-trigger-header"></trigger-header>
                    <single-alert-state-changer *ngIf="alert && alert.currentState !== 'ESCALATED'"
                                                [alertId]="alert.id"
                                                [currentState]="alert.currentState"
                                                [resultTimestamp]="alert.resultTimestamp"
                                                [trigger]="trigger"
                                                (onStateChanged)="onAlertStateChanged.emit()">
                    </single-alert-state-changer>
                </div>
                <section id="right-alert-list">
                    <trigger-details [currentTrigger]="trigger">
                    </trigger-details>

                    <div class="sequence-alert-container" *ngIf="trigger.type==='sequence'">
                        <sequence-alert-result
                            [trigger]="trigger"
                            [alertResult]="alert"
                            [showAlertStoryLink]="true">
                        </sequence-alert-result>
                    </div>
                    <ng-container *ngIf="trigger.type!=='sequence'">
                        <div id="primary-term-container" *ngIf="(savedQuery$ | async)?.primaryField">
                            <div class="primary-term row">
                                <span class="info-title medium-3 small-16">
                                    {{ (savedQuery$ | async)?.primaryField }}
                                </span>
                                <div class="medium-13 small-16 columns">
                                    <a
                                       dsRouterLink="."
                                       [queryParams]="{ mode: 'alert_story', selected: alert.primaryTerm }"
                                       queryParamsHandling="merge"
                                       test-class="link-text trigger-name">{{ alert.primaryTerm }}</a>
                                </div>
                            </div>
                        </div>
                        <div class="anomaly-row" *ngIf="trigger.type === TriggerType.patternscoutSmartThreshold">
                            <div class="row">
                                <span class="info-title medium-3 small-16">
                                    {{ "ANOMALY" | localize }}
                                </span>
                                <div class="medium-13 small-16 columns">
                                    <anomaly-counts [trigger]="trigger" [alert]="alert"></anomaly-counts>
                                </div>
                            </div>
                        </div>
                        <uba-panels *ngIf="trigger.type === TriggerType.fraudUba" [riskFactors]="alert.riskFactors"></uba-panels>

                        <div class="flex-container space-between events-table-header">
                            <span class="events-table-title">{{ "RECENT_EVENTS" | localize }}</span>
                            <ul class="button-group right download-button-group">
                                <li>
                                    <ds-button size="large"
                                               [icon]="true"
                                               [disabled]="downloadingEvents"
                                               (clicked)="downloadCsv()"
                                               [matTooltip]="'GENERIC_DOWNLOAD_CSV' | localize"
                                               test-class="download-csv">
                                        <i [ngClass]="downloadingEvents ? 'far fa-spinner fa-spin' : 'fas fa-cloud-download'"></i>
                                    </ds-button>
                                </li>
                            </ul>
                        </div>
                        <alert-events-table class="full-width"
                                            [alert]="alert"
                                            [trigger]="trigger">
                        </alert-events-table>
                    </ng-container>
                </section>
            </div>
        </ng-container>
    `,
    styles: [`
        @import "/src/styles/colors";
        @import "/src/styles/settings";

        .anomaly-row {
            width: 100%;
            margin-left: 0;
            padding-left: 1.25rem;
            padding-right: 1.25rem;
        }

        .alert-display-header {
            align-items: center;
            // to mimic the border under h2 in trigger-header because the header doesnt stretch
            // the entire width due to inclusion of alert state change buttons on the same row
            border-bottom: 1px solid $gray-5;
        }

        .alert-display-trigger-header {
            flex-grow: 1;
        }

        .events-table-title {
            font-size: 16px;
            font-weight: $font-weight-bold;
        }

        .events-table-header {
            align-items: center;
        }
    `],
    host: {
        style: "display: contents;",
    },
})
export class SingleAlertDisplayComponent implements OnChanges {
    TriggerType = TriggerType;

    @Input() alertId: uuid;
    @Input() loading: boolean;
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onAlertStateChanged = new EventEmitter<void>();

    alert: InboxAlert | SequenceTriggerAlertResultWithId | AnomalyAlertResult | UbaAlertResult;
    trigger: QueryTrigger;
    innerLoading: boolean;
    downloadingEvents = false;
    savedQuery$: Observable<SavedQuery>;

    constructor(@Inject(PRODUCT_DOMAIN) private inboxDomain: Domain,
                private router: RouterService,
                private alertClient: AlertClientService,
                private searchClient: SearchClientService,
                private notificationsService: NotificationsService,
                private readonly timeService: TimeService,
                private savedQueryService: SavedQueryService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.alertId) {
            if (!this.alertId) {
                this.alert = null;
                return;
            }

            this.innerLoading = true;
            this.alertClient.getAlertDetails(this.alertId)
                .then(alert => {
                    this.alert = alert;
                    return this.alertClient.getTrigger(this.alert.triggerId)
                        .then((trigger): Promise<SequenceTriggerAlertResult[] | AnomalyAlertResult[]
                                | UbaAlertResult[]> => {
                            this.trigger = trigger;
                            this.savedQuery$ = this.trigger.savedQueryId
                                ? this.savedQueryService.getSavedQuery$(this.trigger.savedQueryId)
                                    .pipe(shareReplay(1))
                                : null;

                            const filters = [`alertId:${ this.alertId }`, `triggerId:${ this.trigger.id }`,
                                `state:${ this.alert.currentState }`];

                            if (this.trigger.type === "sequence") {
                                return this.alertClient.getSequenceAlertPage(1, filters);
                            } else if (this.trigger.type === TriggerType.patternscoutSmartThreshold) {
                                return this.alertClient.getAnomalyAlertPage(1, filters);
                            } else if (this.trigger.type === "fraud_uba") {
                                return this.alertClient.getUbaAlertPage(1, filters);
                            }
                        })
                        .then(alertPage => {
                            if (alertPage) {
                                this.alert = {
                                    ...alertPage[0],
                                    id: alertPage[0].alertId,
                                };
                            }
                        });
                })
                .catch(error => {
                    if (error instanceof HttpErrorResponse && error.status === 404) {
                        this.notificationsService.showError("INBOX_ALERT_NOT_FOUND", error);
                        this.router.mergeParams<StateParams>({ selected: null });
                    } else {
                        const errorMessage = getMessageForError(error.error, "ERROR_LOADING_ALERT");
                        this.notificationsService.showError(errorMessage, error);
                    }
                })
                .finally(() => this.innerLoading = false);
        }
    }

    downloadCsv() {
        if (this.savedQuery$) {
            this.savedQuery$
                .pipe(take(1))
                .subscribe(query => {
                    const request = {
                        queryString: queryStringForAlertResult(this.trigger, query, (this.alert as InboxAlert)),
                        begin: moment(this.alert.resultTimestamp),
                        end: (this.alert as InboxAlert).resultEndTimestamp
                            ? moment((this.alert as InboxAlert).resultEndTimestamp)
                            : moment(this.alert.resultTimestamp).add(1, "days"),
                        pageSize: 50_000,
                    };
                    this.downloadingEvents = true;
                    this.searchClient.downloadCsv(request)
                        .then(blob => saveAs(blob, `DefenseStorm-AlertEvents-${ this.timeService.now().format(this.timeService.formats.timestampDate) }-${ this.trigger.name }.csv`))
                        .catch(error => {
                            const message = getMessageForError(error.error, "CSV_DOWNLOAD_ERROR");
                            this.notificationsService.showError(message, error);
                        })
                        .finally(() => this.downloadingEvents = false);
                });
        } else {
            // means the trigger does not have the savedQueryId field
            this.notificationsService.showError("TRIGGER_MISSING_SAVED_QUERY");
        }
    }
}
